import React from 'react';
import { Link } from 'react-router-dom';

/**
 * MockTestSection - Showcases Mock Test benefits
 * 
 * AI IMAGE PROMPT for mock-test-feature.jpg:
 * "Modern exam simulation showing a confident student taking a timed mock test on a computer, 
 * with a clean digital timer, progress bar, and analytics dashboard visible on screen. 
 * Split-screen view showing exam interface and real-time performance graphs. 
 * Professional blue and teal gradient background, realistic 3D style, focused lighting, 
 * serious academic atmosphere"
 */

const MockTestSection: React.FC = () => {
  const features = [
    {
      icon: 'fas fa-stopwatch',
      title: 'Timed Practice',
      description: 'Simulate real exam conditions with accurate time limits',
      color: '#3498db'
    },
    {
      icon: 'fas fa-clipboard-check',
      title: 'Full-Length Tests',
      description: 'Complete exam pattern coverage with all sections',
      color: '#2ecc71'
    },
    {
      icon: 'fas fa-chart-bar',
      title: 'Instant Analysis',
      description: 'Detailed performance report after each attempt',
      color: '#e74c3c'
    },
    {
      icon: 'fas fa-graduation-cap',
      title: 'Exam Readiness',
      description: 'Build confidence and reduce exam-day anxiety',
      color: '#f39c12'
    }
  ];

  const testTypes = [
    { name: 'JEE Main', count: '50+ Tests', icon: 'fas fa-calculator' },
    { name: 'JEE Advanced', count: '30+ Tests', icon: 'fas fa-atom' },
    { name: 'NEET', count: '50+ Tests', icon: 'fas fa-heartbeat' }
  ];

  return (
    <section className="mock-test-section py-5" style={{ background: 'linear-gradient(135deg, #3498db15 0%, #2ecc7115 100%)' }}>
      <div className="container">
        <div className="row align-items-center">
          {/* Left - Image */}
          <div className="col-lg-6 mb-4 mb-lg-0" data-aos="fade-right">
            <div className="position-relative">
              <img 
                src="/img/mock-test-feature.jpg" 
                alt="Mock Test Platform" 
                className="img-fluid rounded-4 shadow-lg"
                style={{ width: '100%', height: 'auto' }}
              />
              {/* Floating Timer Badge */}
              <div 
                className="position-absolute top-0 start-0 m-4 px-4 py-3 rounded-4 shadow-lg"
                style={{ background: 'white' }}
              >
                <div className="d-flex align-items-center">
                  <i className="fas fa-clock text-primary fa-2x me-3"></i>
                  <div>
                    <div className="fw-bold" style={{ fontSize: '1.2rem' }}>3:00:00</div>
                    <small className="text-muted">Exam Duration</small>
                  </div>
                </div>
              </div>

              {/* Floating Progress Badge */}
              <div 
                className="position-absolute bottom-0 end-0 m-4 px-4 py-3 rounded-4 shadow-lg"
                style={{ background: 'linear-gradient(135deg, #3498db, #2ecc71)', color: 'white' }}
              >
                <div>
                  <div className="fw-bold">Real Exam Environment</div>
                  <small>Complete simulation</small>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Content */}
          <div className="col-lg-6" data-aos="fade-left">
            <div className="ps-lg-4">
              {/* Badge */}
              <span 
                className="badge rounded-pill px-3 py-2 mb-3"
                style={{ background: '#3498db20', color: '#3498db', fontSize: '14px', fontWeight: 600 }}
              >
                <i className="fas fa-clipboard-list me-2"></i>Full-Length Mock Tests
              </span>

              {/* Heading */}
              <h2 className="fw-bold mb-3" style={{ fontSize: '2.5rem', lineHeight: '1.2' }}>
                Experience <span style={{ color: '#3498db' }}>Real Exam</span> Conditions
              </h2>

              {/* Description */}
              <p className="text-muted mb-4" style={{ fontSize: '1.1rem' }}>
                Take comprehensive mock tests that replicate the actual JEE/NEET exam environment. 
                Practice time management, test your knowledge, and identify areas for improvement 
                before the real exam day.
              </p>

              {/* Test Types */}
              <div className="row g-3 mb-4">
                {testTypes.map((test, index) => (
                  <div key={index} className="col-4">
                    <div 
                      className="text-center p-3 rounded-4"
                      style={{ background: 'white', border: '2px solid #3498db20' }}
                    >
                      <i className={`${test.icon} fa-2x mb-2`} style={{ color: '#3498db' }}></i>
                      <div className="fw-bold small">{test.name}</div>
                      <small className="text-muted">{test.count}</small>
                    </div>
                  </div>
                ))}
              </div>

              {/* Features Grid */}
              <div className="row g-3 mb-4">
                {features.map((feature, index) => (
                  <div key={index} className="col-6">
                    <div 
                      className="p-3 rounded-4"
                      style={{ background: 'white', border: `2px solid ${feature.color}20` }}
                    >
                      <div 
                        className="rounded-circle d-inline-flex align-items-center justify-content-center mb-2"
                        style={{ width: '40px', height: '40px', background: feature.color + '20', color: feature.color }}
                      >
                        <i className={feature.icon}></i>
                      </div>
                      <h6 className="fw-bold mb-1 small">{feature.title}</h6>
                      <small className="text-muted" style={{ fontSize: '0.85rem' }}>
                        {feature.description}
                      </small>
                    </div>
                  </div>
                ))}
              </div>

              {/* Key Highlight */}
              <div 
                className="p-3 rounded-4 mb-4"
                style={{ background: 'linear-gradient(135deg, #3498db15, #2ecc7115)', border: '2px solid #3498db30' }}
              >
                <div className="d-flex align-items-center">
                  <i className="fas fa-lightbulb fa-2x me-3" style={{ color: '#f39c12' }}></i>
                  <div>
                    <strong>Pro Tip:</strong> Students who take 10+ mock tests score 
                    <span className="fw-bold text-success"> 30% higher</span> on average!
                  </div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="d-flex gap-3">
                <Link 
                  to="/mock-quiz" 
                  className="btn btn-lg rounded-pill px-4"
                  style={{ background: 'linear-gradient(135deg, #3498db, #2ecc71)', color: 'white', border: 'none' }}
                >
                  <i className="fas fa-play-circle me-2"></i>
                  Start Mock Test
                </Link>
                <Link 
                  to="/performance" 
                  className="btn btn-lg btn-outline-primary rounded-pill px-4"
                >
                  View Results
                  <i className="fas fa-arrow-right ms-2"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MockTestSection;
